import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Alert,
  Popover,
  Menu,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  FileUpload as ImportIcon,
  ViewList as ColumnsIcon,
  Download as ExportIcon,
  VisibilityOutlined as ViewIcon,
  EditOutlined as EditIcon,
  MoreVert as MoreVertIcon,
  CalendarMonth as CalendarIcon,
  Close as CloseIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import { useThemeMode } from "../../contexts/ThemeModeContext";
import { patientService } from "../../services/ris";
import { calculateAge, formatDicomDate } from "../../services/utils";
import PatientRegistration from "../Clinician/PatientRegistration";
import PatientHistoryPanel from "../Common/PatientHistoryPanel";
import { exportToCSV } from "../../utils/exportUtils";
import ColumnSelectorPopover from "../Common/ColumnSelectorPopover";
import ImportPatientsDialog from "../Common/ImportPatientsDialog";

const PATIENT_COLUMNS = [
  { key: 'id', label: 'Patient ID' },
  { key: 'name', label: 'Name', required: true },
  { key: 'genderAge', label: 'Gender / Age' },
  { key: 'phone', label: 'Phone' },
  { key: 'lastExam', label: 'Last Exam' },
  { key: 'totalExams', label: 'Total Exams' },
  { key: 'status', label: 'Status' },
  { key: 'consent', label: 'Consent' },
  { key: 'actions', label: 'Actions', required: true },
];

// NDPA 2023 s.26 — consent badge state for a patient row
const getConsentState = (p) => {
  const c = p && p.consent;
  if (!c || c.consentGiven !== true) {
    return { label: 'Not Captured', bg: '#FEF2F2', color: '#B91C1C' };
  }
  if (c.withdrawn) {
    return { label: 'Withdrawn', bg: '#FFFBEB', color: '#B45309' };
  }
  return { label: 'Consented', bg: '#ECFDF5', color: '#047857' };
};

const getDisplayName = (p) => {
  if (p.firstName || p.lastName) {
    return [p.firstName, p.lastName].filter(Boolean).join(" ");
  }
  return p.patientName || p.name || "-";
};

const getGenderAge = (p) => {
  const sex = p.sex || p.patientSex || "U";
  const age = p.birthDate ? calculateAge(p.birthDate) : null;
  return age != null ? `${sex} / ${age} Y` : sex;
};

const formatSexLabel = (sex) => {
  if (sex === "M") return "Male";
  if (sex === "F") return "Female";
  if (sex === "O") return "Other";
  return "-";
};

const PatientsView = ({ systemSource, onSelectPatient, onCreateOrder } = {}) => {
  const { mode } = useThemeMode();
  const isDark = mode === "dark";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0";
  const textPrimary = isDark ? "#F8FAFC" : "#0F172A";
  const textSecondary = isDark ? "#94A3B8" : "#64748B";
  const cardBg = isDark ? "#1E1E1E" : "#FFFFFF";
  const inputBg = isDark ? "#121212" : "#FFFFFF";

  const [tabValue, setTabValue] = useState(0);
  const [patientGender, setPatientGender] = useState("All");
  const [dateRange, setDateRange] = useState("All Time");
  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [ageRange, setAgeRange] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [patients, setPatients] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  // Stable counts for the period tabs (All / Today / This Week / This Month).
  // Loaded independently of tabValue so the numbers don't change when a tab is clicked.
  const [tabCounts, setTabCounts] = useState({ all: 0, today: 0, week: 0, month: 0 });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  // NDPA s.35 consent withdrawal confirmation
  const [withdrawTarget, setWithdrawTarget] = useState(null);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawError, setWithdrawError] = useState('');
  const [patientEditForm, setPatientEditForm] = useState({
    firstName: "",
    lastName: "",
    middleName: "",
    sex: "M",
    birthDate: "",
    telephone: "",
    email: "",
    address: "",
  });
  const [saving, setSaving] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [columnsAnchor, setColumnsAnchor] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem('dycovue_patients_columns');
      return saved ? JSON.parse(saved) : PATIENT_COLUMNS.map(c => c.key);
    } catch (e) {
      return PATIENT_COLUMNS.map(c => c.key);
    }
  });

  const handleToggleColumn = (key) => {
    setVisibleColumns(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      try { localStorage.setItem('dycovue_patients_columns', JSON.stringify(next)); } catch (e) {}
      return next;
    });
  };

  const handleResetColumns = () => {
    const all = PATIENT_COLUMNS.map(c => c.key);
    setVisibleColumns(all);
    try { localStorage.setItem('dycovue_patients_columns', JSON.stringify(all)); } catch (e) {}
  };

  const handleExportPatients = () => {
    const exportCols = [
      { key: 'mrn', label: 'Patient ID', formatter: (v, p) => p.mrn || p.patientId || p._id },
      { key: 'name', label: 'Full Name', formatter: (v, p) => getDisplayName(p) },
      { key: 'sex', label: 'Gender', formatter: (v, p) => formatSexLabel(p.sex || p.patientSex) },
      { key: 'birthDate', label: 'Date of Birth', formatter: (v, p) => p.birthDate ? formatDicomDate(p.birthDate) : '' },
      { key: 'phone', label: 'Phone', formatter: (v, p) => p.telephone || p.phone || '' },
      { key: 'email', label: 'Email', formatter: (v, p) => p.email || '' },
      { key: 'nationalId', label: 'National ID', formatter: (v, p) => p.nationalId || '' },
      { key: 'address', label: 'Address', formatter: (v, p) => p.address || '' },
      { key: 'lastStudyDate', label: 'Last Exam Date', formatter: (v, p) => p.lastStudyDate ? new Date(p.lastStudyDate).toLocaleDateString() : '' },
      { key: 'totalExams', label: 'Total Exams', formatter: (v, p) => p.totalExams ?? p.studyCount ?? 0 },
      { key: 'status', label: 'Status', formatter: (v, p) => p.deleted ? 'Inactive' : p.status || 'Active' },
      { key: 'createdAt', label: 'Registered Date', formatter: (v, p) => p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '' },
    ];
    exportToCSV(patients, `patients_export_${new Date().toISOString().split('T')[0]}`, exportCols);
  };

  // Patient History Drawer & Row Context Menu
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyPatient, setHistoryPatient] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuPatient, setMenuPatient] = useState(null);

  const handleOpenMenu = (event, patient) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuPatient(patient);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuPatient(null);
  };

  const handleOpenHistory = (patient) => {
    setHistoryPatient(patient);
    setHistoryOpen(true);
    handleCloseMenu();
  };

  // Open the consent-withdrawal confirmation dialog
  const handleWithdrawConsent = (patient) => {
    setWithdrawTarget(patient);
    setWithdrawReason('');
    setWithdrawError('');
  };

  // Confirm withdrawal — flags consent, retains records (NDPA s.24(1)(d), s.35)
  const confirmWithdrawConsent = async () => {
    if (!withdrawTarget) return;
    try {
      setWithdrawing(true);
      setWithdrawError('');
      await patientService.withdrawConsent(
        withdrawTarget._id,
        withdrawReason.trim() || 'Patient withdrew consent'
      );
      setWithdrawTarget(null);
      setWithdrawReason('');
      fetchPatients();
    } catch (err) {
      setWithdrawError(err.response?.data?.error || 'Failed to withdraw consent');
    } finally {
      setWithdrawing(false);
    }
  };

  const fetchPatients = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: rowsPerPage, page: page + 1 };
      if (searchQuery.trim()) params.q = searchQuery.trim();
      if (patientGender && patientGender !== "All") {
        params.sex =
          patientGender === "Male"
            ? "M"
            : patientGender === "Female"
              ? "F"
              : undefined;
      }
      if (dateRange === "Today") params.period = "today";
      else if (dateRange === "Last 7 Days" || dateRange === "This Week") params.period = "week";
      else if (dateRange === "This Month") params.period = "month";
      else if (dateRange === "This Year") {
        params.period = "year";
        params.year = new Date().getFullYear();
      } else if (dateRange && dateRange.startsWith("Year ")) {
        params.period = "year";
        params.year = dateRange.replace("Year ", "").trim();
      }
      else if (dateRange === "All Time" && tabValue === 1) params.period = "today";
      else if (dateRange === "All Time" && tabValue === 2) params.period = "week";
      else if (dateRange === "All Time" && tabValue === 3) params.period = "month";

      params.systemSource = systemSource || "RIS";

      const res = await patientService.search(params);
      const data = res?.data || res;
      let items =
        data?.patients || data?.items || (Array.isArray(data) ? data : []);

      // Client-side age filtering if selected
      if (ageRange && ageRange !== "All" && Array.isArray(items)) {
        items = items.filter((p) => {
          const birth = p.birthDate ? new Date(p.birthDate) : null;
          let age = p.age;
          if (age === undefined && birth && !isNaN(birth.getTime())) {
            const today = new Date();
            age = today.getFullYear() - birth.getFullYear();
          }
          if (age === undefined || age === null) return true;
          if (ageRange === "0-18") return age >= 0 && age <= 18;
          if (ageRange === "19-40") return age >= 19 && age <= 40;
          if (ageRange === "41-65") return age >= 41 && age <= 65;
          if (ageRange === "66+") return age >= 66;
          return true;
        });
      }

      setPatients(Array.isArray(items) ? items : []);
      setTotalCount(data?.total ?? items.length ?? 0);
    } catch (err) {
      console.error("Failed to fetch patients:", err);
      setPatients([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    rowsPerPage,
    searchQuery,
    patientGender,
    dateRange,
    ageRange,
    statusFilter,
    tabValue,
    systemSource,
  ]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  // ── Stable period-tab counts ───────────────────────────────────────────────
  // Deliberately independent of tabValue: each period count is fetched once per
  // filter change, so clicking a tab never disturbs the other tabs' numbers.
  const countsKey = `${systemSource || "RIS"}|${searchQuery}|${patientGender}|${statusFilter}|${ageRange}|${dateRange}`;
  useEffect(() => {
    let cancelled = false;
    const base = { systemSource: systemSource || "RIS", limit: 1 };
    if (searchQuery.trim()) base.q = searchQuery.trim();
    if (patientGender && patientGender !== "All") {
      base.sex = patientGender === "Male" ? "M" : patientGender === "Female" ? "F" : undefined;
    }
    const totalOf = (r) => {
      const d = r?.data || r;
      const arr = d?.patients || d?.items || (Array.isArray(d) ? d : []);
      return d?.total ?? arr.length ?? 0;
    };
    (async () => {
      try {
        const [all, today, week, month] = await Promise.all([
          patientService.search({ ...base }),
          patientService.search({ ...base, period: "today" }),
          patientService.search({ ...base, period: "week" }),
          patientService.search({ ...base, period: "month" }),
        ]);
        if (!cancelled) {
          setTabCounts({
            all: totalOf(all),
            today: totalOf(today),
            week: totalOf(week),
            month: totalOf(month),
          });
        }
      } catch (e) {
        // Non-critical — keep the previous counts on failure
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countsKey]);

  const handlePaginationChange = (e, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleOpenViewPatient = (patient) => {
    setSelectedPatient(patient);
    setViewDialogOpen(true);
  };

  const handleOpenEditPatient = (patient) => {
    setSelectedPatient(patient);
    setPatientEditForm({
      firstName: patient.firstName || "",
      lastName: patient.lastName || "",
      middleName: patient.middleName || "",
      sex: patient.sex || patient.patientSex || "M",
      birthDate: patient.birthDate || "",
      telephone: patient.telephone || patient.phone || "",
      email: patient.email || "",
      address: patient.address || "",
      status: patient.deleted ? "Inactive" : patient.status || "Active",
    });
    setEditDialogOpen(true);
  };

  return (
    <Box sx={{ width: "100%", minWidth: 0, p: systemSource === "LIS" ? 0 : { xs: 1, sm: 1.5 }, bgcolor: "transparent" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: textPrimary,
              fontFamily: "'Satoshi', sans-serif",
              fontSize: "1.15rem",
              lineHeight: 1.2,
            }}
          >
            Patients
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: textSecondary, fontSize: "0.75rem" }}
          >
            Search, view and manage patient records ({totalCount} total)
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ImportIcon sx={{ fontSize: 15 }} />}
            onClick={() => setImportDialogOpen(true)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: textSecondary,
              borderColor: borderColor,
              bgcolor: cardBg,
              borderRadius: "6px",
              height: 28,
              fontSize: "0.75rem",
              px: 1.25,
              "&:hover": {
                borderColor: "#2563EB",
                bgcolor: isDark ? "rgba(37,99,235,0.1)" : "#F8FAFC",
              },
            }}
          >
            Import
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={() => setRegisterOpen(true)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              bgcolor: "#2563EB",
              "&:hover": { bgcolor: "#1D4ED8" },
              borderRadius: "6px",
              height: 28,
              fontSize: "0.75rem",
              px: 1.5,
            }}
          >
            Register Patient
          </Button>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: "4px 8px",
          mb: 0.75,
          bgcolor: cardBg,
          border: `1px solid ${borderColor}`,
          boxShadow: "none",
          borderRadius: "6px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
          }}
        >
          <TextField
            placeholder="Name, Patient ID, Phone..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchPatients()}
            sx={{
              minWidth: "200px",
              flex: 1,
              maxWidth: 320,
              "& .MuiOutlinedInput-root": {
                borderRadius: "5px",
                bgcolor: inputBg,
                height: 26,
                fontSize: "0.75rem",
                color: textPrimary,
                "& fieldset": { borderColor: borderColor },
                "&:hover fieldset": { borderColor: "#3B82F6" },
              },
              "& .MuiOutlinedInput-input": {
                py: "2px",
                px: 1,
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: textSecondary, fontSize: 15 }} />
                </InputAdornment>
              ),
            }}
          />

          <Button
            onClick={(e) => setDateAnchorEl(e.currentTarget)}
            startIcon={<CalendarIcon fontSize="small" sx={{ color: textSecondary, fontSize: 14 }} />}
            sx={{
              borderRadius: "5px",
              border: `1px solid ${borderColor}`,
              textTransform: "none",
              color: textPrimary,
              px: 1,
              height: 26,
              fontSize: "0.75rem",
              fontFamily: "'Satoshi', sans-serif",
              fontWeight: 500,
              minWidth: "120px",
              justifyContent: "flex-start",
              bgcolor: inputBg,
              "&:hover": {
                borderColor: "#3B82F6",
                bgcolor: isDark ? "rgba(255,255,255,0.06)" : "#F8FAFC",
              },
            }}
          >
            {dateRange === "All Time" ? "All Time" : dateRange}
          </Button>

          <Popover
            open={Boolean(dateAnchorEl)}
            anchorEl={dateAnchorEl}
            onClose={() => setDateAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            PaperProps={{
              sx: {
                p: 1,
                bgcolor: isDark ? "#1E1E1E" : "#FFFFFF",
                borderRadius: "8px",
                border: `1px solid ${borderColor}`,
                boxShadow: isDark
                  ? "0 4px 20px rgba(0,0,0,0.5)"
                  : "0 4px 20px rgba(15,23,42,0.08)",
              },
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: "0.75rem", color: textPrimary, mb: 0.5 }}>
              Select Date Range
            </Typography>
            {[
              { label: "Today", value: "Today" },
              { label: "Last 7 Days", value: "Last 7 Days" },
              { label: "This Month", value: "This Month" },
              {
                label: `This Year (${new Date().getFullYear()})`,
                value: "This Year",
              },
              {
                label: `Year ${new Date().getFullYear() - 1}`,
                value: `Year ${new Date().getFullYear() - 1}`,
              },
              {
                label: `Year ${new Date().getFullYear() - 2}`,
                value: `Year ${new Date().getFullYear() - 2}`,
              },
              { label: "All Time", value: "All Time" },
            ].map((opt) => (
              <MenuItem
                key={opt.value}
                onClick={() => {
                  setDateRange(opt.value);
                  setDateAnchorEl(null);
                  setPage(0);
                }}
                sx={{
                  fontSize: "0.75rem",
                  borderRadius: "4px",
                  py: 0.5,
                  fontWeight: dateRange === opt.value ? 700 : 400,
                  color: textPrimary,
                  bgcolor:
                    dateRange === opt.value
                      ? isDark
                        ? "rgba(37,99,235,0.15)"
                        : "#EFF6FF"
                      : "transparent",
                }}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Popover>

          <FormControl size="small" sx={{ minWidth: "95px" }}>
            <Select
              displayEmpty
              value={patientGender}
              onChange={(e) => {
                setPatientGender(e.target.value);
                setPage(0);
              }}
              renderValue={(selected) => (
                <span style={{ color: selected === "All" ? textSecondary : textPrimary }}>
                  {selected === "All" ? "Gender: All" : selected}
                </span>
              )}
              sx={{
                borderRadius: "5px",
                bgcolor: inputBg,
                height: 26,
                fontSize: "0.75rem",
                color: textPrimary,
                "& .MuiSelect-select": { py: "2px", px: "8px" },
                "& fieldset": { borderColor: borderColor },
                "&:hover fieldset": { borderColor: "#3B82F6" },
                "& .MuiSvgIcon-root": { color: textSecondary, fontSize: 16 },
              }}
            >
              <MenuItem value="All" sx={{ fontSize: "0.75rem" }}>Gender: All</MenuItem>
              <MenuItem value="Male" sx={{ fontSize: "0.75rem" }}>Male</MenuItem>
              <MenuItem value="Female" sx={{ fontSize: "0.75rem" }}>Female</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: "95px" }}>
            <Select
              displayEmpty
              value={ageRange}
              onChange={(e) => {
                setAgeRange(e.target.value);
                setPage(0);
              }}
              renderValue={(selected) => (
                <span style={{ color: selected === "All" ? textSecondary : textPrimary }}>
                  {selected === "All" ? "Age: All" : `Age: ${selected}`}
                </span>
              )}
              sx={{
                borderRadius: "5px",
                bgcolor: inputBg,
                height: 26,
                fontSize: "0.75rem",
                color: textPrimary,
                "& .MuiSelect-select": { py: "2px", px: "8px" },
                "& fieldset": { borderColor: borderColor },
                "&:hover fieldset": { borderColor: "#3B82F6" },
                "& .MuiSvgIcon-root": { color: textSecondary, fontSize: 16 },
              }}
            >
              <MenuItem value="All" sx={{ fontSize: "0.75rem" }}>Age: All</MenuItem>
              <MenuItem value="0-18" sx={{ fontSize: "0.75rem" }}>0-18</MenuItem>
              <MenuItem value="19-40" sx={{ fontSize: "0.75rem" }}>19-40</MenuItem>
              <MenuItem value="41-65" sx={{ fontSize: "0.75rem" }}>41-65</MenuItem>
              <MenuItem value="66+" sx={{ fontSize: "0.75rem" }}>66+</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: "95px" }}>
            <Select
              displayEmpty
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              renderValue={(selected) => (
                <span style={{ color: selected === "All" ? textSecondary : textPrimary }}>
                  {selected === "All" ? "Status: All" : selected}
                </span>
              )}
              sx={{
                borderRadius: "5px",
                bgcolor: inputBg,
                height: 26,
                fontSize: "0.75rem",
                color: textPrimary,
                "& .MuiSelect-select": { py: "2px", px: "8px" },
                "& fieldset": { borderColor: borderColor },
                "&:hover fieldset": { borderColor: "#3B82F6" },
                "& .MuiSvgIcon-root": { color: textSecondary, fontSize: 16 },
              }}
            >
              <MenuItem value="All" sx={{ fontSize: "0.75rem" }}>Status: All</MenuItem>
              <MenuItem value="Active" sx={{ fontSize: "0.75rem" }}>Active</MenuItem>
              <MenuItem value="Inactive" sx={{ fontSize: "0.75rem" }}>Inactive</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ ml: "auto", display: "flex", gap: 0.75, alignItems: "center" }}>
            <Button
              size="small"
              sx={{ textTransform: "none", color: textSecondary, fontWeight: 600, height: 26, fontSize: "0.72rem", px: 1 }}
              onClick={() => {
                setSearchQuery("");
                setPatientGender("All");
                setDateRange("All Time");
                setAgeRange("All");
                setStatusFilter("All");
                setPage(0);
              }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<FilterIcon sx={{ fontSize: 14 }} />}
              onClick={() => {
                setPage(0);
                fetchPatients();
              }}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "#2563EB",
                borderRadius: "5px",
                height: 26,
                fontSize: "0.72rem",
                px: 1.25,
                "&:hover": { bgcolor: "#1D4ED8" },
              }}
            >
              Filter
            </Button>
          </Box>
        </Box>
      </Paper>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 0.75,
        }}
      >
        <Tabs
          value={tabValue}
          onChange={(e, val) => {
            setTabValue(val);
            setPage(0);
          }}
          sx={{
            minHeight: 26,
            "& .MuiTabs-indicator": {
              backgroundColor: "#2563EB",
              height: "2px",
            },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.75rem",
              color: textSecondary,
              minWidth: "auto",
              minHeight: 26,
              py: 0,
              px: 1.25,
              "&.Mui-selected": { color: "#2563EB" },
            },
          }}
        >
          <Tab label={`All Patients (${tabCounts.all})`} />
          <Tab label={`Today (${tabCounts.today})`} />
          <Tab label={`This Week (${tabCounts.week})`} />
          <Tab label={`This Month (${tabCounts.month})`} />
        </Tabs>
        <Box sx={{ display: "flex", gap: 0.75 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ColumnsIcon sx={{ fontSize: 14 }} />}
            onClick={(e) => setColumnsAnchor(e.currentTarget)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: textSecondary,
              borderColor: borderColor,
              bgcolor: cardBg,
              borderRadius: "5px",
              height: 26,
              fontSize: "0.72rem",
              px: 1,
              "&:hover": {
                borderColor: "#2563EB",
                bgcolor: isDark ? "rgba(37,99,235,0.1)" : "#EFF6FF",
              },
            }}
          >
            Columns
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ExportIcon sx={{ fontSize: 14 }} />}
            onClick={handleExportPatients}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: textSecondary,
              borderColor: borderColor,
              bgcolor: cardBg,
              borderRadius: "5px",
              height: 26,
              fontSize: "0.72rem",
              px: 1,
              "&:hover": {
                borderColor: "#2563EB",
                bgcolor: isDark ? "rgba(37,99,235,0.1)" : "#EFF6FF",
              },
            }}
          >
            Export
          </Button>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          bgcolor: cardBg,
          border: `1px solid ${borderColor}`,
          boxShadow: "none",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <TableContainer
          sx={{
            overflowX: "auto",
            width: "100%",
            "&::-webkit-scrollbar": { height: "6px" },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: isDark ? "rgba(255,255,255,0.2)" : "#CBD5E1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-track": {
              bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#F1F5F9",
            },
          }}
        >
          <Table size="small" sx={{ minWidth: 950 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: isDark ? "#171717" : "#F8FAFC", height: 28 }}>
                {visibleColumns.includes('id') && (
                  <TableCell sx={{ fontWeight: 700, color: textSecondary, fontSize: "0.68rem", py: '3px !important', px: '6px !important', borderBottom: `1px solid ${borderColor}`, textTransform: 'uppercase' }}>Patient ID</TableCell>
                )}
                {visibleColumns.includes('name') && (
                  <TableCell sx={{ fontWeight: 700, color: textSecondary, fontSize: "0.68rem", py: '3px !important', px: '6px !important', borderBottom: `1px solid ${borderColor}`, textTransform: 'uppercase' }}>Name</TableCell>
                )}
                {visibleColumns.includes('genderAge') && (
                  <TableCell sx={{ fontWeight: 700, color: textSecondary, fontSize: "0.68rem", py: '3px !important', px: '6px !important', borderBottom: `1px solid ${borderColor}`, textTransform: 'uppercase' }}>Gender / Age</TableCell>
                )}
                {visibleColumns.includes('phone') && (
                  <TableCell sx={{ fontWeight: 700, color: textSecondary, fontSize: "0.68rem", py: '3px !important', px: '6px !important', borderBottom: `1px solid ${borderColor}`, textTransform: 'uppercase' }}>Phone</TableCell>
                )}
                {visibleColumns.includes('lastExam') && (
                  <TableCell sx={{ fontWeight: 700, color: textSecondary, fontSize: "0.68rem", py: '3px !important', px: '6px !important', borderBottom: `1px solid ${borderColor}`, textTransform: 'uppercase' }}>Last Exam</TableCell>
                )}
                {visibleColumns.includes('totalExams') && (
                  <TableCell sx={{ fontWeight: 700, color: textSecondary, fontSize: "0.68rem", py: '3px !important', px: '6px !important', textAlign: "center", borderBottom: `1px solid ${borderColor}`, textTransform: 'uppercase' }}>Total Exams</TableCell>
                )}
                {visibleColumns.includes('status') && (
                  <TableCell sx={{ fontWeight: 700, color: textSecondary, fontSize: "0.68rem", py: '3px !important', px: '6px !important', borderBottom: `1px solid ${borderColor}`, textTransform: 'uppercase' }}>Status</TableCell>
                )}
                {visibleColumns.includes('consent') && (
                  <TableCell sx={{ fontWeight: 700, color: textSecondary, fontSize: "0.68rem", py: '3px !important', px: '6px !important', borderBottom: `1px solid ${borderColor}`, textTransform: 'uppercase' }}>Consent</TableCell>
                )}
                {visibleColumns.includes('actions') && (
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: textSecondary,
                      fontSize: "0.68rem",
                      py: '3px !important',
                      px: '6px !important',
                      textAlign: "right",
                      borderBottom: `1px solid ${borderColor}`,
                      position: "sticky",
                      right: 0,
                      bgcolor: isDark ? "#171717" : "#F8FAFC",
                      zIndex: 2,
                      boxShadow: isDark ? "-4px 0 8px rgba(0,0,0,0.4)" : "-4px 0 8px rgba(0,0,0,0.04)",
                      minWidth: 140,
                      textTransform: 'uppercase'
                    }}
                  >
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length || 8} align="center" sx={{ py: 3, borderBottom: `1px solid ${borderColor}` }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : patients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length || 8} align="center" sx={{ py: 3, color: textSecondary, fontSize: "0.75rem", borderBottom: `1px solid ${borderColor}` }}>
                    No patients found
                  </TableCell>
                </TableRow>
              ) : (
                patients.map((patient) => {
                  const statusVal = patient.deleted ? "Inactive" : patient.status || "Active";
                  const isInactive = statusVal === "Inactive";
                  return (
                    <TableRow key={patient._id} hover sx={{ height: 28, bgcolor: cardBg, "&:hover": { bgcolor: isDark ? "#262626 !important" : "#F8FAFC !important" } }}>
                      {visibleColumns.includes('id') && (
                        <TableCell sx={{ fontSize: "0.75rem", py: '2px !important', px: '6px !important', color: textSecondary, borderBottom: `1px solid ${borderColor}`, whiteSpace: 'nowrap' }}>
                          {patient.mrn || patient.patientId || patient._id}
                        </TableCell>
                      )}
                      {visibleColumns.includes('name') && (
                        <TableCell sx={{ fontSize: "0.75rem", py: '2px !important', px: '6px !important', fontWeight: 700, color: textPrimary, borderBottom: `1px solid ${borderColor}`, whiteSpace: 'nowrap' }}>
                          {getDisplayName(patient)}
                        </TableCell>
                      )}
                      {visibleColumns.includes('genderAge') && (
                        <TableCell sx={{ fontSize: "0.75rem", py: '2px !important', px: '6px !important', color: textSecondary, borderBottom: `1px solid ${borderColor}`, whiteSpace: 'nowrap' }}>
                          {getGenderAge(patient)}
                        </TableCell>
                      )}
                      {visibleColumns.includes('phone') && (
                        <TableCell sx={{ fontSize: "0.75rem", py: '2px !important', px: '6px !important', color: textSecondary, borderBottom: `1px solid ${borderColor}`, whiteSpace: 'nowrap' }}>
                          {patient.telephone || patient.phone || "-"}
                        </TableCell>
                      )}
                      {visibleColumns.includes('lastExam') && (
                        <TableCell sx={{ fontSize: "0.75rem", py: '2px !important', px: '6px !important', color: textSecondary, borderBottom: `1px solid ${borderColor}`, whiteSpace: 'nowrap' }}>
                          <Typography component="span" sx={{ fontSize: "0.75rem", color: textPrimary, fontWeight: 600, fontFamily: "'Satoshi', sans-serif" }}>
                            {patient.lastStudyDate ? new Date(patient.lastStudyDate).toLocaleDateString() : "-"}
                          </Typography>
                          {patient.lastModality && (
                            <Typography component="span" sx={{ fontSize: "0.68rem", color: textSecondary, ml: 0.5 }}>
                              · {patient.lastModality}
                            </Typography>
                          )}
                        </TableCell>
                      )}
                      {visibleColumns.includes('totalExams') && (
                        <TableCell sx={{ fontSize: "0.75rem", py: '2px !important', px: '6px !important', color: textPrimary, fontWeight: 700, textAlign: "center", borderBottom: `1px solid ${borderColor}`, whiteSpace: 'nowrap' }}>
                          {patient.totalExams ?? patient.studyCount ?? "-"}
                        </TableCell>
                      )}
                      {visibleColumns.includes('status') && (
                        <TableCell sx={{ py: '2px !important', px: '6px !important', borderBottom: `1px solid ${borderColor}`, whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: "inline-flex", px: 0.75, py: '1px', borderRadius: "4px", fontSize: "0.68rem", fontWeight: 600, bgcolor: isInactive ? (isDark ? "rgba(100,116,139,0.15)" : "#F3F4F6") : (isDark ? "rgba(5, 150, 105, 0.15)" : "#ECFDF5"), color: isInactive ? (isDark ? "#94A3B8" : "#64748B") : "#059669" }}>
                            {statusVal}
                          </Box>
                        </TableCell>
                      )}
                      {visibleColumns.includes('consent') && (() => {
                        const cs = getConsentState(patient);
                        return (
                          <TableCell sx={{ py: '2px !important', px: '6px !important', borderBottom: `1px solid ${borderColor}`, whiteSpace: 'nowrap' }}>
                            <Box title={patient.consent?.withdrawnReason || undefined} sx={{ display: "inline-flex", px: 0.75, py: '1px', borderRadius: "4px", fontSize: "0.68rem", fontWeight: 600, bgcolor: isDark ? 'rgba(255,255,255,0.06)' : cs.bg, color: isDark ? cs.color : cs.color }}>
                              {cs.label}
                            </Box>
                          </TableCell>
                        );
                      })()}
                      {visibleColumns.includes('actions') && (
                        <TableCell
                          sx={{
                            textAlign: "right",
                            py: '2px !important',
                            px: '6px !important',
                            borderBottom: `1px solid ${borderColor}`,
                            position: "sticky",
                            right: 0,
                            bgcolor: cardBg,
                            zIndex: 1,
                            boxShadow: isDark ? "-4px 0 8px rgba(0,0,0,0.4)" : "-4px 0 8px rgba(0,0,0,0.04)",
                            minWidth: 140,
                            "tr:hover &": {
                              bgcolor: isDark ? "#262626 !important" : "#F8FAFC !important",
                            },
                          }}
                        >
                        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.5 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ViewIcon sx={{ fontSize: '13px !important' }} />}
                            onClick={() => handleOpenViewPatient(patient)}
                            sx={{
                              textTransform: "none",
                              fontSize: "0.68rem",
                              fontWeight: 600,
                              color: textSecondary,
                              borderColor: borderColor,
                              borderRadius: "4px",
                              height: 22,
                              px: 0.75,
                              "&:hover": {
                                color: "#2563EB",
                                borderColor: "#2563EB",
                                bgcolor: isDark ? "rgba(37,99,235,0.1)" : "#EFF6FF",
                              },
                            }}
                          >
                            View
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon sx={{ fontSize: '13px !important' }} />}
                            onClick={() => handleOpenEditPatient(patient)}
                            sx={{
                              textTransform: "none",
                              fontSize: "0.68rem",
                              fontWeight: 600,
                              color: textSecondary,
                              borderColor: borderColor,
                              borderRadius: "4px",
                              height: 22,
                              px: 0.75,
                              "&:hover": {
                                color: "#2563EB",
                                borderColor: "#2563EB",
                                bgcolor: isDark ? "rgba(37,99,235,0.1)" : "#EFF6FF",
                              },
                            }}
                          >
                            Edit
                          </Button>
                          {patient.consent?.consentGiven === true && !patient.consent?.withdrawn && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleWithdrawConsent(patient)}
                              title="Withdraw patient consent (NDPA s.35)"
                              sx={{
                                textTransform: "none",
                                fontSize: "0.68rem",
                                fontWeight: 600,
                                color: "#B45309",
                                borderColor: isDark ? "rgba(180,83,9,0.4)" : "#FCD34D",
                                borderRadius: "4px",
                                height: 22,
                                px: 0.75,
                                minWidth: 0,
                                "&:hover": {
                                  color: "#92400E",
                                  borderColor: "#D97706",
                                  bgcolor: isDark ? "rgba(217,119,6,0.12)" : "#FFFBEB",
                                },
                              }}
                            >
                              Withdraw
                            </Button>
                          )}
                          <Tooltip title="Options & History">
                            <IconButton
                              size="small"
                              onClick={(e) => handleOpenMenu(e, patient)}
                              sx={{
                                color: textSecondary,
                                border: `1px solid ${borderColor}`,
                                borderRadius: "4px",
                                width: 22,
                                height: 22,
                                p: 0,
                                "&:hover": {
                                  color: "#2563EB",
                                  borderColor: "#2563EB",
                                  bgcolor: isDark ? "rgba(37,99,235,0.1)" : "#EFF6FF",
                                },
                              }}
                            >
                              <MoreVertIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 20, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePaginationChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          sx={{
            borderTop: `1px solid ${borderColor}`,
            bgcolor: cardBg,
            color: textSecondary,
            minHeight: 32,
            "& .MuiToolbar-root": {
              minHeight: "32px !important",
              height: 32,
              px: 1,
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              color: textSecondary,
              fontSize: "0.72rem",
              m: 0,
            },
            "& .MuiTablePagination-select": {
              color: textPrimary,
              fontSize: "0.72rem",
              py: "2px",
            },
            "& .MuiTablePagination-actions .MuiIconButton-root": {
              color: textSecondary,
              p: "2px",
              "&.Mui-disabled": {
                color: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
              },
            },
          }}
        />
      </Paper>

      {/* View Patient Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: "95vw", sm: "740px", md: "780px" },
            maxWidth: "800px !important",
            maxHeight: "88vh",
            borderRadius: "14px",
            bgcolor: cardBg,
            backgroundImage: "none",
            border: `1px solid ${borderColor}`,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            p: 0,
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${borderColor}`, px: 2.5, py: 1.25 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: textPrimary, fontFamily: "'Satoshi', sans-serif", fontSize: "0.95rem" }}>
              Patient Details
            </Typography>
            <IconButton
              onClick={() => setViewDialogOpen(false)}
              size="small"
              sx={{ color: textSecondary, p: 0.5 }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor, px: 2.5, py: 1.75, overflowY: "auto" }}>
          {selectedPatient && (
            <Grid container spacing={1.75} sx={{ mt: 0 }}>
              <Grid item xs={6} sm={3}>
                <Typography
                  variant="caption"
                  sx={{ color: textSecondary, fontWeight: 700, display: "block", mb: 0.25, fontSize: "0.7rem" }}
                >
                  PATIENT ID (MRN)
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: textPrimary, fontSize: "0.8rem" }}
                >
                  {selectedPatient.mrn || selectedPatient.patientId || "-"}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography
                  variant="caption"
                  sx={{ color: textSecondary, fontWeight: 700, display: "block", mb: 0.25, fontSize: "0.7rem" }}
                >
                  FULL NAME
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: textPrimary, fontSize: "0.8rem" }}
                >
                  {getDisplayName(selectedPatient)}
                </Typography>
              </Grid>
              {selectedPatient.middleName ? (
                <Grid item xs={6} sm={3}>
                  <Typography
                    variant="caption"
                    sx={{ color: textSecondary, fontWeight: 700, display: "block", mb: 0.25, fontSize: "0.7rem" }}
                  >
                    MIDDLE NAME
                  </Typography>
                  <Typography variant="body2" sx={{ color: textPrimary, fontSize: "0.8rem" }}>
                    {selectedPatient.middleName}
                  </Typography>
                </Grid>
              ) : null}
              <Grid item xs={6} sm={3}>
                <Typography
                  variant="caption"
                  sx={{ color: textSecondary, fontWeight: 700, display: "block", mb: 0.25, fontSize: "0.7rem" }}
                >
                  GENDER
                </Typography>
                <Typography variant="body2" sx={{ color: textPrimary, fontSize: "0.8rem" }}>
                  {formatSexLabel(
                    selectedPatient.sex || selectedPatient.patientSex,
                  )}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography
                  variant="caption"
                  sx={{ color: textSecondary, fontWeight: 700, display: "block", mb: 0.25, fontSize: "0.7rem" }}
                >
                  AGE / DATE OF BIRTH
                </Typography>
                <Typography variant="body2" sx={{ color: textPrimary, fontSize: "0.8rem" }}>
                  {selectedPatient.birthDate
                    ? `${formatDicomDate(selectedPatient.birthDate)} (${calculateAge(selectedPatient.birthDate)} Y)`
                    : "-"}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography
                  variant="caption"
                  sx={{ color: textSecondary, fontWeight: 700, display: "block", mb: 0.25, fontSize: "0.7rem" }}
                >
                  PHONE NUMBER
                </Typography>
                <Typography variant="body2" sx={{ color: textPrimary, fontSize: "0.8rem" }}>
                  {selectedPatient.telephone || selectedPatient.phone || "-"}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography
                  variant="caption"
                  sx={{ color: textSecondary, fontWeight: 700, display: "block", mb: 0.25, fontSize: "0.7rem" }}
                >
                  EMAIL ADDRESS
                </Typography>
                <Typography variant="body2" sx={{ color: textPrimary, fontSize: "0.8rem", wordBreak: "break-all" }}>
                  {selectedPatient.email || "-"}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography
                  variant="caption"
                  sx={{ color: textSecondary, fontWeight: 700, display: "block", mb: 0.25, fontSize: "0.7rem" }}
                >
                  STATUS
                </Typography>
                <Typography variant="body2" sx={{ color: textPrimary, fontSize: "0.8rem" }}>
                  {selectedPatient.deleted
                    ? "Inactive"
                    : selectedPatient.status || "Active"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={selectedPatient.middleName ? 12 : 6}>
                <Typography
                  variant="caption"
                  sx={{ color: textSecondary, fontWeight: 700, display: "block", mb: 0.25, fontSize: "0.7rem" }}
                >
                  ADDRESS
                </Typography>
                <Typography variant="body2" sx={{ color: textPrimary, fontSize: "0.8rem" }}>
                  {selectedPatient.address || "-"}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.25, borderTop: `1px solid ${borderColor}`, gap: 1 }}>
          <Button
            size="small"
            onClick={() => setViewDialogOpen(false)}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: "6px",
              borderColor,
              color: textSecondary,
              height: 30,
              fontSize: "0.78rem",
              px: 2,
              "&:hover": { borderColor: "#94A3B8" },
            }}
          >
            Close
          </Button>
          {(onCreateOrder || onSelectPatient) && selectedPatient && (
            <Button
              size="small"
              onClick={() => {
                const p = selectedPatient;
                setViewDialogOpen(false);
                if (onCreateOrder) onCreateOrder(p);
                else if (onSelectPatient) onSelectPatient(p);
              }}
              variant="contained"
              sx={{
                textTransform: "none",
                borderRadius: "6px",
                bgcolor: "#00665C",
                height: 30,
                fontSize: "0.78rem",
                px: 2,
                "&:hover": { bgcolor: "#004D47" },
              }}
            >
              Create Order
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <PatientRegistration
        open={editDialogOpen}
        editPatient={selectedPatient}
        onClose={() => setEditDialogOpen(false)}
        systemSource={systemSource}
        onSuccess={() => {
          setEditDialogOpen(false);
          fetchPatients();
        }}
        onCreateOrder={(patient) => {
          setEditDialogOpen(false);
          fetchPatients();
          if (onCreateOrder) {
            onCreateOrder(patient);
          } else if (onSelectPatient) {
            onSelectPatient(patient);
          }
        }}
      />

      <PatientRegistration
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        systemSource={systemSource}
        onSuccess={() => {
          setRegisterOpen(false);
          fetchPatients();
        }}
        onCreateOrder={(patient) => {
          setRegisterOpen(false);
          fetchPatients();
          if (onCreateOrder) {
            onCreateOrder(patient);
          } else if (onSelectPatient) {
            onSelectPatient(patient);
          }
        }}
      />

      {/* Patient Options Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {
            borderRadius: "10px",
            bgcolor: cardBg,
            border: `1px solid ${borderColor}`,
            boxShadow: isDark
              ? "0 4px 20px rgba(0,0,0,0.6)"
              : "0 4px 20px rgba(15,23,42,0.08)",
            minWidth: 190,
            py: 0.5,
          },
        }}
      >
        {(onCreateOrder || onSelectPatient) && (
          <MenuItem
            onClick={() => {
              const p = menuPatient;
              handleCloseMenu();
              if (p) {
                if (onCreateOrder) onCreateOrder(p);
                else if (onSelectPatient) onSelectPatient(p);
              }
            }}
            sx={{
              fontSize: "0.85rem",
              fontWeight: 700,
              color: "#00665C",
              gap: 1.5,
              py: 1,
              "&:hover": {
                bgcolor: isDark ? "rgba(0,102,92,0.15)" : "#F0FDF4",
              },
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" />
              <line x1="9" y1="15" x2="15" y2="15" />
            </svg>
            Create Order
          </MenuItem>
        )}
        <MenuItem
          onClick={() => handleOpenHistory(menuPatient)}
          sx={{
            fontSize: "0.85rem",
            fontWeight: 600,
            color: textPrimary,
            gap: 1.5,
            py: 1,
            "&:hover": {
              bgcolor: isDark ? "rgba(0,102,92,0.15)" : "#F0FDF4",
              color: "#00665C",
            },
          }}
        >
          <HistoryIcon fontSize="small" sx={{ color: "#00665C" }} />
          Patient History
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuPatient) handleOpenViewPatient(menuPatient);
            handleCloseMenu();
          }}
          sx={{
            fontSize: "0.85rem",
            fontWeight: 500,
            color: textPrimary,
            gap: 1.5,
            py: 1,
            "&:hover": {
              bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#F8FAFC",
            },
          }}
        >
          <ViewIcon fontSize="small" sx={{ color: textSecondary }} />
          View Details
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuPatient) handleOpenEditPatient(menuPatient);
            handleCloseMenu();
          }}
          sx={{
            fontSize: "0.85rem",
            fontWeight: 500,
            color: textPrimary,
            gap: 1.5,
            py: 1,
            "&:hover": {
              bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#F8FAFC",
            },
          }}
        >
          <EditIcon fontSize="small" sx={{ color: textSecondary }} />
          Edit Patient
        </MenuItem>
      </Menu>

      {/* Patient History Drawer */}
      <PatientHistoryPanel
        open={historyOpen}
        onClose={() => {
          setHistoryOpen(false);
          setHistoryPatient(null);
        }}
        patientId={
          historyPatient?.mrn ||
          historyPatient?.patientId ||
          historyPatient?._id
        }
        patientName={historyPatient ? getDisplayName(historyPatient) : ""}
        patientBirthDate={historyPatient?.birthDate}
        patientSex={historyPatient?.sex || historyPatient?.patientSex}
      />

      {/* Column Visibility Selector Popover */}
      <ColumnSelectorPopover
        open={Boolean(columnsAnchor)}
        anchorEl={columnsAnchor}
        onClose={() => setColumnsAnchor(null)}
        columns={PATIENT_COLUMNS}
        visibleColumns={visibleColumns}
        onToggle={handleToggleColumn}
        onReset={handleResetColumns}
        isDark={isDark}
      />

      {/* CSV Patient Import Dialog */}
      <ImportPatientsDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImportComplete={(count) => {
          fetchPatients();
        }}
        isDark={isDark}
      />

      {/* Consent Withdrawal Confirmation (NDPA s.35) */}
      <Dialog
        open={Boolean(withdrawTarget)}
        onClose={() => (withdrawing ? null : setWithdrawTarget(null))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', fontFamily: "'Satoshi', sans-serif" }}>
          Withdraw Patient Consent
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, fontFamily: "'Satoshi', sans-serif" }}>
            Withdrawing consent stops non-essential processing for{' '}
            <strong>{withdrawTarget ? getDisplayName(withdrawTarget) : ''}</strong>. Clinical records are
            <strong> retained</strong> as required by medical-records and legal obligations
            (NDPA 2023 s.24(1)(d), s.35). This action is logged.
          </Alert>
          {withdrawError && <Alert severity="error" sx={{ mb: 2 }}>{withdrawError}</Alert>}
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Reason for withdrawal"
            value={withdrawReason}
            onChange={(e) => setWithdrawReason(e.target.value)}
            placeholder="e.g. Patient request received by phone on 12 Oct"
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            onClick={() => setWithdrawTarget(null)}
            disabled={withdrawing}
            variant="outlined"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmWithdrawConsent}
            disabled={withdrawing}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#B45309', '&:hover': { bgcolor: '#92400E' } }}
          >
            {withdrawing ? 'Withdrawing…' : 'Confirm Withdrawal'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientsView;
